/**
 * Lean starter graph for the editable `b2b-users` stage on /architecture/map.
 *
 * Loaded the first time the stage is visited (when `b2b_users_nodes` is empty).
 * The meeting fills in the rest — drag-connect to draw arrows, double-click to
 * rename, toolbar to add components/actors, Reset returns to this seed.
 */

import type { Domain } from "@/presentation/data/domains";

export type B2bUsersNodeKind = "system" | "actor";

export interface B2bUsersNode {
  id: string;
  label: string;
  role: string;
  domain: Domain;
  kind: B2bUsersNodeKind;
  x: number;
  y: number;
}

export type B2bUsersEdgeKind =
  | "primary"
  | "manual"
  | "bidirectional"
  | "dependency"
  | "finance"
  | "feedback";

export interface B2bUsersEdge {
  id: string;
  source: string;
  target: string;
  kind: B2bUsersEdgeKind;
  label?: string | null;
}

/* Layout: actors on left column, systems in a horizontal pipeline. */
const COL_ACTOR_X = 80;
const COL_APP_X = 520;
const COL_BACK_X = 960;
const ROW_TOP_Y = 80;
const ROW_MID_Y = 280;
const ROW_BOT_Y = 480;

export const B2B_USERS_SEED_NODES: readonly B2bUsersNode[] = [
  {
    id: "actor-b2b-buyer",
    label: "B2B Buyer",
    role: "Purchasing / kitchen manager placing orders",
    domain: "app-core",
    kind: "actor",
    x: COL_ACTOR_X,
    y: ROW_MID_Y,
  },
  {
    id: "actor-account-manager",
    label: "Account Manager",
    role: "Dedicated AM for a B2B client",
    domain: "customer-experience",
    kind: "actor",
    x: COL_ACTOR_X,
    y: ROW_BOT_Y,
  },
  {
    id: "b2b-mobile-app",
    label: "B2B Mobile App",
    role: "Customer-facing B2B ordering app (iOS / Android)",
    domain: "app-core",
    kind: "system",
    x: COL_APP_X,
    y: ROW_MID_Y,
  },
  {
    id: "b2b-onboarding-portal",
    label: "B2B Onboarding Portal",
    role: "Account creation, KYB, credit-limit setup",
    domain: "app-core",
    kind: "system",
    x: COL_APP_X,
    y: ROW_TOP_Y,
  },
  {
    id: "b2b-pricing-engine",
    label: "B2B Pricing Engine",
    role: "Per-client discounts, ex-VAT presentation",
    domain: "pricing-commerce",
    kind: "system",
    x: COL_BACK_X,
    y: ROW_TOP_Y,
  },
  {
    id: "am-dashboard",
    label: "AM Dashboard",
    role: "AM workspace — assigned clients, queue, escalation",
    domain: "customer-experience",
    kind: "system",
    x: COL_APP_X,
    y: ROW_BOT_Y,
  },
  {
    id: "b2b-customer-crm",
    label: "B2B Customer CRM",
    role: "Per-business record — orders, refunds, credit, notes",
    domain: "customer-experience",
    kind: "system",
    x: COL_BACK_X,
    y: ROW_BOT_Y,
  },
];

export const B2B_USERS_SEED_EDGES: readonly B2bUsersEdge[] = [
  {
    id: "e-buyer-app",
    source: "actor-b2b-buyer",
    target: "b2b-mobile-app",
    kind: "primary",
    label: "place / track orders",
  },
  {
    id: "e-app-onboarding",
    source: "b2b-mobile-app",
    target: "b2b-onboarding-portal",
    kind: "primary",
    label: "sign-up",
  },
  {
    id: "e-app-pricing",
    source: "b2b-mobile-app",
    target: "b2b-pricing-engine",
    kind: "primary",
    label: "per-client prices",
  },
  {
    id: "e-app-am",
    source: "b2b-mobile-app",
    target: "am-dashboard",
    kind: "bidirectional",
    label: "in-app chat",
  },
  {
    id: "e-am-crm",
    source: "am-dashboard",
    target: "b2b-customer-crm",
    kind: "primary",
  },
  {
    id: "e-am-actor",
    source: "actor-account-manager",
    target: "am-dashboard",
    kind: "primary",
  },
];
